<template>
  <div style="border-radius: 9px" class="pa-4 white">
    <!-- Header -->
    <div class="mb-4">
      <div class="d-flex flex-wrap" style="gap: 8px">
        <div class="main-toolbar">
          <div class="icon-back">
            <v-img
              src="../../../assets/figmaIcons/white/big-data.svg"
              max-width="55%"
            ></v-img>
          </div>
          <span class="main-toolbar-title">Big Data Dashboard</span>
        </div>
      </div>
    </div>

    <!-- Tabs (mobile: icons + tooltip) -->
    <div class="hidden-md-and-up">
      <v-tabs
        :height="40"
        v-model="reportTab"
        icons-and-text
        touchless
        grow
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tooltip
          v-for="(tab, i) in tabConfigs"
          :key="'mob-' + i"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tab
              :value="i"
              active-class="activated-tab"
              class="rounded-lg rounded-b-0"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>{{ tab.icon }}</v-icon>
            </v-tab>
          </template>
          <span>{{ tab.title }}</span>
        </v-tooltip>
      </v-tabs>
    </div>

    <!-- Tabs (desktop: icons + text) -->
    <div class="hidden-sm-and-down">
      <v-tabs
        :height="40"
        v-model="reportTab"
        icons-and-text
        touchless
        grow
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tab
          v-for="(tab, i) in tabConfigs"
          :key="'desk-' + i"
          :value="i"
          active-class="activated-tab"
          class="rounded-lg rounded-b-0"
        >
          {{ tab.title }}
          <v-icon class="mt-1 mr-2">{{ tab.icon }}</v-icon>
        </v-tab>
      </v-tabs>
    </div>

    <!-- Dark visualization panel -->
    <div class="dark-panel">
      <v-row class="ma-0" align="start">
        <!-- Left: Filters -->
        <v-col cols="12" md="3" class="pa-2">
          <template v-if="isAdminOrMaster">
            <label class="panel-label">Master / Administrador</label>
            <v-autocomplete
              hide-details
              v-model="selectedAdminUserId"
              :items="adminUsers"
              item-text="displayName"
              item-value="id"
              :loading="adminUsersLoading"
              placeholder="Todos"
              outlined
              dense
              dark
              clearable
              class="panel-input"
              @change="onAdminUserChange"
            ></v-autocomplete>

            <div v-if="selectedAdminUserId" class="d-flex mt-2" style="gap: 6px">
              <v-btn x-small depressed color="#bbcf20" style="color: #0f3565; font-weight: 600"
                class="flex-grow-1" @click="savePreset" :loading="savingPreset">
                <v-icon x-small left>mdi-content-save</v-icon>
                {{ selectedAdminHasPreset ? 'Atualizar' : 'Salvar' }} preset
              </v-btn>
              <v-btn v-if="selectedAdminHasPreset" x-small depressed outlined dark
                class="flex-grow-1" @click="removePreset" :loading="savingPreset">
                <v-icon x-small left>mdi-delete</v-icon> Limpar 
              </v-btn> 
            </div>

            <div v-if="selectedAdminUserId && selectedAdminHasPreset" class="mt-1">
              <v-chip x-small dark color="rgba(187,207,32,0.2)" text-color="#bbcf20">
                <v-icon x-small left>mdi-check-circle</v-icon> Preset salvo
              </v-chip>
            </div>

            <label class="panel-label mt-3">Supervisor</label>
            <v-autocomplete
              hide-details
              v-model="selectedSupervisorId"
              :items="filteredSupervisors"
              item-text="name"
              item-value="id"
              :loading="supervisorsLoading"
              placeholder="Todos"
              outlined
              dense
              dark
              clearable
              class="panel-input"
              @change="onSupervisorChange"
            ></v-autocomplete>
          </template>

          <label class="panel-label" :class="{ 'mt-3': isAdminOrMaster }">Instituição</label>
          <v-autocomplete
            :search-input.sync="institutionSearch"
            @blur="institutionSearch = ''"
            hide-details
            v-model="chartFilter.institutionIds"
            :items="filteredInstitutionOptions"
            multiple
            item-text="name"
            @change="getUnidades(true)"
            item-value="id"
            :loading="institutionLoading"
            outlined
            dense
            dark
            class="panel-input"
            :placeholder="chartFilter.institutionIds.length === 0 ? 'Todas' : ''"
            :menu-props="{ maxHeight: 350 }"
          >
            <template v-slot:prepend-item>
              <v-list-item @click="toggleSelectAll">
                <v-list-item-action>
                  <v-icon :color="chartFilter.institutionIds.length > 0 ? '#bbcf20' : ''">
                    {{ selectAllIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Selecionar todas</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-1"></v-divider>
            </template>
            <template v-slot:selection="{ index }">
              <span v-if="index === 0" class="white--text" style="font-size: 13px">
                {{ chartFilter.institutionIds.length }} selecionada(s)
              </span>
            </template>
          </v-autocomplete>

          <label class="panel-label mt-3">Unidade</label>
          <v-autocomplete
            hide-details
            v-model="chartFilter.unidadeIds"
            :items="unidades"
            multiple
            item-value="id"
            item-text="name"
            :loading="unidadeLoading"
            placeholder="Todas"
            outlined
            dense
            dark
            clearable
            class="panel-input"
            :menu-props="{ maxHeight: 350 }"
          >
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on" v-bind="attrs">
                <v-list-item-action>
                  <v-simple-checkbox :value="attrs.inputValue" @click.stop="on.click"></v-simple-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.name }}<span v-if="item.institution && item.institution.initials" class="black--text ml-1" style="font-size: 11px;">[{{ item.institution.initials }}]</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:selection="{ index }">
              <span v-if="index === 0" class="white--text" style="font-size: 13px">
                {{ chartFilter.unidadeIds.length }} selecionada(s)
              </span>
            </template>
          </v-autocomplete>

          <label class="panel-label mt-3">Período</label>
          <div class="d-flex align-center" style="gap: 6px">
            <v-menu
              ref="chartStartMenu"
              :close-on-content-click="false"
              :return-value.sync="chartFilter.startDate"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatMonthShort(chartFilter.startDate)"
                  readonly outlined dense dark hide-details
                  class="panel-input" v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="chartFilter.startDate" type="month"
                :locale="$i18n.locale" no-title scrollable
                @change="$refs.chartStartMenu.save(chartFilter.startDate)"
              ></v-date-picker>
            </v-menu>
            <span class="white--text" style="font-size: 12px; white-space: nowrap">até</span>
            <v-menu
              ref="chartEndMenu"
              :close-on-content-click="false"
              :return-value.sync="chartFilter.endDate"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatMonthShort(chartFilter.endDate)"
                  readonly outlined dense dark hide-details
                  class="panel-input" v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="chartFilter.endDate" type="month"
                :locale="$i18n.locale" no-title scrollable
                @change="$refs.chartEndMenu.save(chartFilter.endDate)"
              ></v-date-picker>
            </v-menu>
          </div>

          <div class="d-flex mt-3" style="gap: 8px">
            <v-btn small depressed color="#bbcf20" class="flex-grow-1"
              style="color: #0f3565; font-weight: 600" @click="applyFilter">
              <v-icon small left>mdi-magnify</v-icon> Filtrar
            </v-btn>
            <v-btn small depressed outlined dark class="flex-grow-1" @click="resetFilter">
              <v-icon small left>mdi-cached</v-icon> Limpar
            </v-btn>
          </div>
        </v-col>

        <!-- Right: Title + KPIs + Gauges + Chart -->
        <v-col cols="12" md="9" class="pa-2">
          <h2 class="text-center white--text panel-title">
            <transition name="fade-title" mode="out-in">
              <span :key="reportTab">{{ activeTabConfig.title }}</span>
            </transition>
          </h2>

          <!-- Loading -->
          <div v-if="chartLoading" class="d-flex justify-center align-center" style="height: 500px">
            <v-progress-circular indeterminate color="#bbcf20" size="48"></v-progress-circular>
          </div>

          <!-- No data -->
          <div v-else-if="!hasData" class="text-center pa-8">
            <v-icon dark size="48" class="mb-3" style="opacity: 0.4">mdi-chart-bar</v-icon>
            <div class="white--text" style="opacity: 0.6">
              Nenhum dado encontrado. Selecione os filtros e clique em "Filtrar".
            </div>
          </div>

          <template v-if="hasData && !chartLoading">
            <!-- KPI Cards row -->
            <transition name="fade-content" mode="out-in">
              <div :key="'kpi-' + reportTab" class="kpi-row mb-3">
                <div v-for="(kpi, ki) in kpiCards" :key="ki" class="kpi-card">
                  <div class="kpi-value" :style="{ color: kpi.color || '#fff' }">{{ kpi.value }}</div>
                  <div class="kpi-label">{{ kpi.label }}</div>
                </div>
              </div>
            </transition>

            <!-- Gauges + Bar chart row -->
            <v-row class="ma-0" no-gutters>
              <!-- Gauges (left) - only for schedule and resolvability -->
              <v-col v-if="activeTabConfig.type !== 'satisfaction'" cols="12" md="3" class="d-flex flex-column align-center justify-center">
                <div class="gauge-title">% de conclusão interna</div>
                <v-chart
                  :key="'gauge-int-' + reportTab"
                  class="gauge-chart"
                  :option="gaugeInternalOption"
                />
                <div class="gauge-title mt-2">% de conclusão total</div>
                <v-chart
                  :key="'gauge-tot-' + reportTab"
                  class="gauge-chart"
                  :option="gaugeTotalOption"
                />
              </v-col>

              <!-- Bar chart (right) - scrollable -->
              <v-col :cols="12" :md="activeTabConfig.type !== 'satisfaction' ? 9 : 12">
                <div class="chart-subtitle text-center mb-1">
                  {{ chartSubtitle }}
                </div>
                <div v-if="selectedInstitutionIndex !== null" class="selected-indicator text-center mb-2">
                  <v-chip small dark color="#bbcf20" text-color="#0f3565" close @click:close="selectedInstitutionIndex = null">
                    {{ groupedData.institutions[selectedInstitutionIndex].institutionName }}
                  </v-chip>
                </div>
                <div class="chart-scroll-wrapper">
                  <v-chart
                    ref="mainChart"
                    :key="'chart-' + reportTab"
                    class="main-bar-chart"
                    :style="{ width: chartDynamicWidth + 'px', minWidth: '100%' }"
                    :option="barChartOption"
                    @click="onChartClick"
                  />
                </div>
              </v-col>
            </v-row>

          </template>
        </v-col>
      </v-row>

      <v-row v-if="hasData && !chartLoading && activeTabConfig.type === 'satisfaction'" class="ma-0 mt-2" no-gutters>
        <v-col cols="12" md="3" class="d-flex flex-column align-center justify-center">
          <div class="gauge-title">% de Adesão</div>
          <v-chart
            :key="'gauge-adh-' + reportTab"
            class="gauge-chart adhesion-gauge-chart"
            :option="adhesionGaugeOption"
          />
        </v-col>
        <v-col cols="12" md="9">
          <div class="chart-subtitle text-center mb-1">
            Adesão ao módulo de chamados por instituição
          </div>
          <div class="chart-scroll-wrapper">
            <v-chart
              :key="'chart-adh-' + reportTab"
              class="main-bar-chart"
              :style="{ width: adhesionChartWidth + 'px', minWidth: '100%' }"
              :option="adhesionBarChartOption"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Data table inside panel -->
      <transition name="fade-content" mode="out-in">
        <div v-if="hasData && !chartLoading && tableRows.length > 0" :key="'table-' + reportTab" class="panel-table-wrapper mt-4">
          <table class="panel-table">
            <thead>
              <tr>
                <th class="sticky-col">Instituição</th>
                <th v-for="col in tableColumns" :key="col" class="text-center">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in tableRows" :key="ri" :class="row.class">
                <td class="sticky-col">{{ row.label }}</td>
                <td v-for="(val, vi) in row.values" :key="vi" class="text-center">{{ val }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </transition>
    </div>

    <!-- Análises -->
    <div class="mt-8">
      <div class="d-flex mb-4 align-end">
        <strong class="ml-2" style="font-size: 18px">Análises:</strong>
        <v-spacer></v-spacer>
        <v-btn class="mx-0 primary-button" depressed @click="openAddDialog">Adicionar texto</v-btn>
      </div>
    </div>

    <div class="mt-4">
      <v-col cols="12" class="d-flex align-center justify-center mb-2 flex-wrap">
        <v-menu ref="startMenu" :close-on-content-click="false"
          :return-value.sync="filter.startDate" transition="scale-transition"
          offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-btn depressed class="primary-button mr-0" style="text-transform: none"
              readonly v-bind="attrs" v-on="on">{{ formatMonth(filter.startDate) }}</v-btn>
          </template>
          <v-date-picker v-model="filter.startDate" type="month" :locale="$i18n.locale"
            no-title scrollable @change="$refs.startMenu.save(filter.startDate), getTexts()"></v-date-picker>
        </v-menu>
        <span class="mx-2 subtitle">até</span>
        <v-menu ref="endMenu" :close-on-content-click="false"
          :return-value.sync="filter.endDate" transition="scale-transition"
          offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-btn depressed class="primary-button mr-0" style="text-transform: none"
              readonly v-bind="attrs" v-on="on">{{ formatMonth(filter.endDate) }}</v-btn>
          </template>
          <v-date-picker v-model="filter.endDate" type="month" :locale="$i18n.locale"
            no-title scrollable @change="$refs.endMenu.save(filter.endDate), getTexts()"></v-date-picker>
        </v-menu>
      </v-col>
    </div>

    <div class="my-4">
      <v-data-table :loading="loading" :loading-text="$t('Carregando...')"
        no-data-text="Nenhuma análise encontrada" :headers="headers" :items="items"
        item-key="id" class="main-table" hide-default-footer
        :items-per-page="filter.pageSize" :header-props="headerProps" :mobile-breakpoint="330">
        <template #[`item.reportDate`]="{ item }">
          <span class="text-uppercase">{{ formatMonth(item.reportDate) }}</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn class="mr-2" tile color="#f5f5f5" style="border-radius: 10px; background-color: #f5f5f5"
            rounded icon depressed @click="openViewDialog(item)">
            <v-icon color="#bbcf20">mdi-eye-outline</v-icon>
          </v-btn>
          <v-btn class="secondary-button ml-2" depressed tile rounded icon @click="openEditDialog(item)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn class="ml-2" color="#921616" depressed style="border-radius: 10px; background-color: #921616"
            icon @click="openRemoveDialog(item)">
            <v-icon color="white" small>mdi-trash-can-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <div class="pagination">
        <div class="item-pagination block-one">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" class="btn" depressed color="#C0C0C0" small>qtd: {{ pageSize }}</v-btn>
            </template>
            <v-list>
              <v-list-item v-for="(size, i) in sizes" :key="i" @click="pageSize = size">
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="registros" v-if="totalItensOnPage">
            <span>1 - {{ totalItensOnPage }} de {{ totalItems }}</span><br />
          </div>
        </div>
        <div class="item-pagination block-two">
          <v-pagination class="pagination-item" v-model="page" :length="totalPages" :total-visible="3"></v-pagination>
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <v-dialog v-model="removeDialog" max-width="300">
      <v-card class="pa-4">
        <h2 class="text-center">Remover análise</h2>
        <p class="text-center">Tem certeza de que deseja remover esta análise?</p>
        <div class="d-flex">
          <v-btn color="primary" depressed @click="removeDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed class="mr-0" color="secondary" @click="remove" :loading="loading">Remover</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="850">
      <v-card class="pa-4">
        <h2 class="text-center">Visualização de análise</h2>
        <v-row class="ma-0" justify="center" dense>
          <v-col cols="12" v-if="analysis.author">
            <div><strong>Autor: </strong><span>{{ analysis.author.name }}</span></div>
            <div><strong>E-mail do Autor: </strong><span>{{ analysis.author.email }}</span></div>
          </v-col>
          <v-col cols="12">
            <label class="label ml-1">Mês da análise:</label>
            <div class="d-flex">
              <div class="primary white--text" style="border-radius: 10px; width: 100%; height: 30px; display: flex; justify-content: center; align-items: center; text-transform: uppercase;">
                {{ formatMonth(analysis.reportDate) }}
              </div>
            </div>
          </v-col>
          <v-col cols="12">
            <label class="label ml-1">Texto de análise:</label>
            <div class="pa-4" style="border: 1px solid #ccc; border-radius: 6px" v-html="analysis.text"></div>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <v-dialog v-model="manageDialog" max-width="850">
      <v-card class="pa-4" flat>
        <h2 class="text-center">{{ analysis.id ? 'Editar' : 'Adicionar' }} registro</h2>
        <v-form ref="form">
          <v-row class="ma-0" justify="center" dense>
            <v-col cols="12">
              <label class="label ml-1">Mês da análise:</label>
              <div class="d-flex">
                <v-menu ref="analysisMenu" :close-on-content-click="false"
                  :return-value.sync="analysis.reportDate" transition="scale-transition"
                  offset-y max-width="290px" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn depressed class="primary-button mr-0" style="text-transform: none"
                      readonly block v-bind="attrs" v-on="on">{{ formatMonth(analysis.reportDate) }}</v-btn>
                  </template>
                  <v-date-picker v-model="analysis.reportDate" type="month" :locale="$i18n.locale"
                    no-title scrollable @change="$refs.analysisMenu.save(analysis.reportDate)"></v-date-picker>
                </v-menu>
              </div>
            </v-col>
            <v-col cols="12">
              <label class="label ml-1">Texto de análise:</label>
              <tiptap-vuetify v-model="analysis.text" :rules="[(v) => !!v || 'Campo obrigatório']"
                :extensions="extensions" :label="$t('Análise')"
                :placeholder="$t('Digite suas análises aqui')"
                :card-props="{ flat: true, outlined: true, rounded: true }"
                class="primary-input" outlined dense hide-details="auto"></tiptap-vuetify>
            </v-col>
          </v-row>
        </v-form>
        <div class="d-flex">
          <v-btn color="primary" depressed @click="manageDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed class="mr-0" color="secondary" @click="submit" :loading="loading">
            {{ analysis.id ? 'Editar' : 'Adicionar' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from '../../../services/ApiService'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, GaugeChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import {
  TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code,
  Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote,
  HardBreak, HorizontalRule, History,
} from 'tiptap-vuetify'
import moment from 'moment'

use([
  CanvasRenderer,
  BarChart,
  GaugeChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
])

const COLOR_INTERNAL = '#bbcf20'
const COLOR_TOTAL = '#42a5f5'

const TABS = [
  {
    title: 'Preventiva', icon: 'mdi-wrench-clock',
    endpoint: 'bigData/grouped/preventiveScheduleExecuted',
    type: 'schedule',
  },
  {
    title: 'Calibração', icon: 'mdi-tune-vertical',
    endpoint: 'bigData/grouped/calibrationScheduleExecuted',
    type: 'schedule',
  },
  {
    title: 'Seg. Elétrica', icon: 'mdi-flash-outline',
    endpoint: 'bigData/grouped/electricalSafetyScheduleExecuted',
    type: 'schedule',
  },
  {
    title: 'Corretiva', icon: 'mdi-tools',
    endpoint: 'bigData/grouped/correctiveScheduleExecuted',
    type: 'schedule',
  },
  {
    title: 'Satisfação', icon: 'mdi-emoticon-happy-outline',
    endpoint: 'bigData/grouped/satisfaction',
    type: 'satisfaction',
  },
  {
    title: 'Resolutividade', icon: 'mdi-check-decagram-outline',
    endpoint: 'bigData/grouped/internalResoluteness',
    type: 'resolvability',
  },
]

function formatK(n) {
  return n.toLocaleString('pt-BR')
}

function buildGauge(value, color) {
  return {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 200,
      endAngle: -20,
      min: 0,
      max: 100,
      pointer: { show: false },
      progress: {
        show: true,
        width: 14,
        itemStyle: { color },
      },
      axisLine: {
        lineStyle: { width: 14, color: [[1, 'rgba(255,255,255,0.08)']] },
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        show: true,
        distance: -30,
        fontSize: 10,
        color: 'rgba(255,255,255,0.4)',
        formatter: function (v) {
          if (v === 0 || v === 100) return v.toFixed(2) + '%'
          return ''
        },
      },
      detail: {
        valueAnimation: true,
        fontSize: 22,
        fontWeight: 700,
        color: '#fff',
        offsetCenter: [0, '10%'],
        formatter: '{value}%',
      },
      data: [{ value: value }],
    }],
  }
}

export default {
  components: { TiptapVuetify, VChart },
  provide: { [THEME_KEY]: 'dark' },

  data() {
    return {
      reportTab: 0,
      institutionSearch: '',
      chartFilter: { institutionIds: [], unidadeIds: [], startDate: null, endDate: null },
      institutions: [],
      unidades: [],
      institutionLoading: false,
      unidadeLoading: false,
      selectedSupervisorId: null,
      supervisors: [],
      supervisorsLoading: false,
      selectedAdminUserId: null,
      adminUsers: [],
      adminUsersLoading: false,
      savingPreset: false,
      userIsAdminOrMaster: false,
      chartLoading: false,
      groupedData: null,
      adhesionCalledData: null,
      adhesionToastId: null,
      selectedInstitutionIndex: null,
      // Análises
      removeDialog: false,
      page: 1,
      pageSize: 10,
      sizes: [10, 30, 90, 180],
      totalItensOnPage: null,
      totalItems: null,
      totalPages: null,
      viewDialog: false,
      analysis: { id: null, reportDate: null, type: null, text: null, authorId: null },
      manageDialog: false,
      loading: false,
      items: [],
      headers: [
        { text: '', align: 'center', value: '', sortable: false },
        { text: 'Autor', align: 'center', value: 'author.name', sortable: false },
        { text: 'Data', align: 'center', value: 'reportDate', sortable: false },
        { text: 'Ações', align: 'center', value: 'actions', sortable: false },
      ],
      headerProps: { sortByText: 'Ordenar por' },
      extensions: [
        History, Blockquote, Link, Underline, Strike, Italic, ListItem,
        BulletList, OrderedList, [Heading, { options: { levels: [1, 2, 3] } }],
        Bold, Code, HorizontalRule, Paragraph, HardBreak,
      ],
      apiService: new ApiService(),
      filter: { page: 1, pageSize: 10, startDate: null, endDate: null, type: 0 },
    }
  },

  computed: {
    tabConfigs() { return TABS },
    activeTabConfig() { return TABS[this.reportTab] || TABS[0] },

    isAdminOrMaster() {
      return this.userIsAdminOrMaster
    },

    selectedAdminHasPreset() {
      if (!this.selectedAdminUserId) return false
      const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
      return user && user.hasPreset
    },

    filteredInstitutionOptions() {
      let filtered = this.institutions
      if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.institutionIds) {
          filtered = filtered.filter((i) => adminUser.institutionIds.includes(i.id))
        }
      }
      if (this.selectedSupervisorId) {
        const supervisor = this.supervisors.find((s) => s.id === this.selectedSupervisorId)
        if (supervisor && supervisor.institutionIds) {
          filtered = filtered.filter((i) => supervisor.institutionIds.includes(i.id))
        }
      }
      return filtered
    },

    filteredSupervisors() {
      if (!this.selectedAdminUserId) return this.supervisors
      const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
      if (!adminUser || !adminUser.institutionIds) return this.supervisors
      return this.supervisors.filter((s) =>
        s.institutionIds && s.institutionIds.some((id) => adminUser.institutionIds.includes(id))
      )
    },

    allSelected() {
      return this.filteredInstitutionOptions.length > 0 && this.chartFilter.institutionIds.length === this.filteredInstitutionOptions.length
    },
    someSelected() {
      return this.chartFilter.institutionIds.length > 0 && !this.allSelected
    },
    selectAllIcon() {
      if (this.allSelected) return 'mdi-checkbox-marked'
      if (this.someSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },

    hasData() {
      if (!this.groupedData) return false
      return this.groupedData.institutions && this.groupedData.institutions.length > 0
    },

    filteredInstitutions() {
      if (!this.hasData) return []
      if (this.selectedInstitutionIndex !== null) {
        return [this.groupedData.institutions[this.selectedInstitutionIndex]]
      }
      return this.groupedData.institutions
    },

    aggSchedule() {
      if (!this.hasData || this.activeTabConfig.type !== 'schedule') return null
      let intSch = 0, intExe = 0, totalSch = 0, totalExe = 0
      this.filteredInstitutions.forEach((inst) => {
        (inst.dataByMonth || []).forEach((m) => {
          intSch += m.internalSchedule || 0
          intExe += m.internalExecuted || 0
          totalSch += m.totalSchedule || 0
          totalExe += m.totalExecuted || 0
        })
      })
      const intPct = intSch > 0 ? +(intExe / intSch * 100).toFixed(2) : 0
      const totalPct = totalSch > 0 ? +(totalExe / totalSch * 100).toFixed(2) : 0
      return { intSch, intExe, totalSch, totalExe, intPct, totalPct }
    },

    aggResolvability() {
      if (!this.hasData || this.activeTabConfig.type !== 'resolvability') return null
      let totalInt = 0, totalOs = 0
      this.filteredInstitutions.forEach((inst) => {
        (inst.dataByMonth || []).forEach((m) => {
          totalInt += m.totalInternal || 0
          totalOs += m.totalServiceOrders || 0
        })
      })
      const pct = totalOs > 0 ? +(totalInt / totalOs * 100).toFixed(2) : 0
      return { totalInt, totalOs, pct }
    },

    aggSatisfaction() {
      if (!this.hasData || this.activeTabConfig.type !== 'satisfaction') return null
      let high = 0, well = 0, reg = 0, bad = 0, total = 0
      this.filteredInstitutions.forEach((inst) => {
        high += inst.highRating || 0
        well += inst.wellRating || 0
        reg += inst.regularRating || 0
        bad += inst.badRating || 0
        total += inst.totalRatings || 0
      })
      return { high, well, reg, bad, total }
    },

    kpiCards() {
      const cfg = this.activeTabConfig
      if (cfg.type === 'schedule' && this.aggSchedule) {
        const a = this.aggSchedule
        return [
          { label: 'Agendados Interna', value: formatK(a.intSch), color: COLOR_INTERNAL },
          { label: 'Concluídos Interna', value: formatK(a.intExe), color: COLOR_INTERNAL },
          { label: 'Agendados Total', value: formatK(a.totalSch), color: COLOR_TOTAL },
          { label: 'Concluídos Total', value: formatK(a.totalExe), color: COLOR_TOTAL },
        ]
      }
      if (cfg.type === 'resolvability' && this.aggResolvability) {
        const a = this.aggResolvability
        return [
          { label: 'Concluídos Interno', value: formatK(a.totalInt), color: COLOR_INTERNAL },
          { label: 'Concluídos Total', value: formatK(a.totalOs), color: COLOR_TOTAL },
        ]
      }
      if (cfg.type === 'satisfaction' && this.aggSatisfaction) {
        const a = this.aggSatisfaction
        return [
          { label: 'N. Avaliações', value: formatK(a.total), color: '#fff' },
          { label: 'Ruim', value: formatK(a.bad), color: '#ff5252' },
          { label: 'Regular', value: formatK(a.reg), color: '#ffd740' },
          { label: 'Bom', value: formatK(a.well), color: COLOR_INTERNAL },
          { label: 'Ótimo', value: formatK(a.high), color: '#69f0ae' },
          { label: 'Adesão', value: this.aggAdhesion ? this.aggAdhesion.pct.toFixed(1) + '%' : '-', color: '#26c6da' },
        ]
      }
      return []
    },

    // ── Gauge options ──
    gaugeInternalOption() {
      if (this.activeTabConfig.type === 'schedule' && this.aggSchedule) {
        return buildGauge(this.aggSchedule.intPct, COLOR_INTERNAL)
      }
      if (this.activeTabConfig.type === 'resolvability' && this.aggResolvability) {
        return buildGauge(this.aggResolvability.pct, COLOR_INTERNAL)
      }
      return buildGauge(0, COLOR_INTERNAL)
    },

    gaugeTotalOption() {
      if (this.activeTabConfig.type === 'schedule' && this.aggSchedule) {
        return buildGauge(this.aggSchedule.totalPct, COLOR_TOTAL)
      }
      if (this.activeTabConfig.type === 'resolvability' && this.aggResolvability) {
        return buildGauge(this.aggResolvability.pct, COLOR_TOTAL)
      }
      return buildGauge(0, COLOR_TOTAL)
    },

    aggAdhesion() {
      if (!this.adhesionCalledData || this.activeTabConfig.type !== 'satisfaction') return null
      const { totalCalledsCount, totalOthersCount, totalPct } = this.adhesionCalledData
      return { rated: totalCalledsCount, notRated: totalOthersCount, total: totalCalledsCount + totalOthersCount, pct: totalPct }
    },

    adhesionGaugeOption() {
      const base = this.activeTabConfig.type === 'satisfaction' && this.aggAdhesion
        ? buildGauge(this.aggAdhesion.pct, '#26c6da')
        : buildGauge(0, '#26c6da')
      base.series[0].radius = '85%'
      base.series[0].center = ['50%', '55%']
      return base
    },

    adhesionBarChartOption() {
      if (!this.adhesionCalledData || this.activeTabConfig.type !== 'satisfaction') return {}
      const institutions = this.adhesionCalledData.items || []
      const categories = institutions.map((i) => i.institutionName)
      const ratedData = institutions.map((i) => i.calledsPct || 0)
      const notRatedData = institutions.map((i) => i.othersPct || 0)
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(15,53,101,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#fff', fontSize: 12 },
          confine: true,
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            let result = `${params[0].axisValue}<br/>`
            params.forEach((p) => {
              result += `${p.marker}${p.seriesName}: <b>${p.value}%</b><br/>`
            })
            return result
          },
        },
        legend: {
          top: 4,
          textStyle: { color: 'rgba(255,255,255,0.7)', fontSize: 11 },
        },
        grid: { left: 60, right: 20, top: 36, bottom: 40, containLabel: true },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            color: 'rgba(255,255,255,0.6)',
            fontSize: 10,
            interval: 0,
            rotate: 30,
            formatter: (v) => (v.length > 18 ? v.slice(0, 18) + '…' : v),
          },
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.15)' } },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          axisLabel: {
            color: 'rgba(255,255,255,0.6)',
            fontSize: 10,
            formatter: '{value}%',
          },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.07)' } },
        },
        series: [
          {
            name: this.$t('Via Chamados'),
            type: 'bar',
            stack: 'adhesion',
            data: ratedData,
            barMaxWidth: 28,
            itemStyle: { color: '#26c6da', borderRadius: [0, 0, 0, 0] },
          },
          {
            name: this.$t('Outros Canais'),
            type: 'bar',
            stack: 'adhesion',
            data: notRatedData,
            barMaxWidth: 28,
            itemStyle: { color: 'rgba(255,255,255,0.18)', borderRadius: [3, 3, 0, 0] },
          },
        ],
      }
    },

    chartSubtitle() {
      const cfg = this.activeTabConfig
      if (cfg.type === 'schedule') return '% de conclusão interna e % de conclusão total por instituição'
      if (cfg.type === 'resolvability') return '% de conclusão interna, % de conclusão total e meta por instituição'
      if (cfg.type === 'satisfaction') return '% de satisfação dos atendimentos por instituição'
      return ''
    },

    chartDynamicWidth() {
      if (!this.hasData) return 0
      const instCount = this.groupedData.institutions.length
      const barCount = this.activeTabConfig.type === 'schedule' ? 2 : 1
      const barWidth = 28
      const groupPadding = 40
      const basePadding = 100
      const computed = instCount * (barCount * barWidth + groupPadding) + basePadding
      return Math.max(computed, 400)
    },

    adhesionChartWidth() {
      if (!this.adhesionCalledData || !this.adhesionCalledData.items) return 400
      const instCount = this.adhesionCalledData.items.length
      const computed = instCount * (2 * 28 + 40) + 100
      return Math.max(computed, 400)
    },

    barChartOption() {
      if (!this.hasData) return {}
      const cfg = this.activeTabConfig
      if (cfg.type === 'schedule') return this.scheduleChartOption()
      if (cfg.type === 'resolvability') return this.resolvabilityChartOption()
      if (cfg.type === 'satisfaction') return this.satisfactionChartOption()
      return {}
    },

    tableColumns() {
      if (!this.hasData) return []
      const cfg = this.activeTabConfig
      if (cfg.type === 'schedule') return ['Agend. Interna', 'Exec. Interna', '% Interna', 'Agend. Total', 'Exec. Total', '% Total']
      if (cfg.type === 'resolvability') return ['Internas', 'Total OS', '% Resolutividade']
      if (cfg.type === 'satisfaction') return ['Ótimo', 'Bom', 'Regular', 'Ruim', 'Total', '% Satisfação']
      return []
    },

    tableRows() {
      if (!this.hasData) return []
      const cfg = this.activeTabConfig
      if (cfg.type === 'schedule') return this.scheduleTableRows()
      if (cfg.type === 'resolvability') return this.resolvabilityTableRows()
      if (cfg.type === 'satisfaction') return this.satisfactionTableRows()
      return []
    },
  },

  created() {
    const loggedUser = JSON.parse(localStorage.getItem('user'))
    this.loggedUserId = loggedUser.id
    this.userIsAdminOrMaster = loggedUser.mainType === 0 || loggedUser.mainType === 1
    this.chartFilter.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
    this.chartFilter.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    this.filter.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
    this.filter.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    this.getInstitutions()
    this.fetchTabData()
    this.getTexts()
  },

  methods: {
    toggleSelectAll() {
      if (this.allSelected) {
        this.chartFilter.institutionIds = []
      } else {
        this.chartFilter.institutionIds = this.filteredInstitutionOptions.map((i) => i.id)
      }
      this.getUnidades(true)
    },

    onChartClick(params) {
      if (params.dataIndex === this.selectedInstitutionIndex) {
        this.selectedInstitutionIndex = null
      } else {
        this.selectedInstitutionIndex = params.dataIndex
      }
    },

    buildBarOption(xData, series, legendData, opts = {}) {
      const self = this
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(15,53,101,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#fff', fontSize: 12 },
          confine: true,
          formatter(params) {
            let html = `<strong>${params[0].axisValue}</strong><br/>`
            params.forEach((p) => {
              const raw = p.data && p.data._raw
              const marker = p.marker
              if (raw) {
                if (raw.scheduled !== undefined) {
                  html += `${marker} ${p.seriesName}: ${p.value}%<br/>`
                  html += `&nbsp;&nbsp;&nbsp;Agendados: ${raw.scheduled} | Executados: ${raw.executed}<br/>`
                } else if (raw.internal !== undefined) {
                  html += `${marker} ${p.seriesName}: ${p.value}%<br/>`
                  html += `&nbsp;&nbsp;&nbsp;Internas: ${raw.internal} | Total OS: ${raw.total}<br/>`
                } else if (raw.total !== undefined) {
                  html += `${marker} ${p.seriesName}: ${p.value}%<br/>`
                  html += `&nbsp;&nbsp;&nbsp;Ótimo: ${raw.high} | Bom: ${raw.well} | Regular: ${raw.regular} | Ruim: ${raw.bad}<br/>`
                  html += `&nbsp;&nbsp;&nbsp;Total: ${raw.total}<br/>`
                }
              } else {
                html += `${marker} ${p.seriesName}: ${p.value}<br/>`
              }
            })
            return html
          },
        },
        legend: {
          data: legendData,
          textStyle: { color: 'rgba(255,255,255,0.7)', fontSize: 11 },
          top: 0,
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 14,
        },
        grid: { left: 50, right: 20, top: 40, bottom: 50, containLabel: true },
        xAxis: {
          type: 'category',
          data: xData,
          axisLabel: {
            color: 'rgba(255,255,255,0.65)',
            fontSize: 9,
            rotate: 35,
            interval: 0,
            overflow: 'truncate',
            width: 80,
          },
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.15)' } },
          axisTick: { show: false },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: 'rgba(255,255,255,0.65)', fontSize: 11, formatter: opts.yFormatter || '{value}' },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: self.selectedInstitutionIndex !== null
          ? series.map((s) => ({
            ...s,
            data: s.data.map((d, i) => {
              const item = typeof d === 'object' ? { ...d } : { value: d }
              if (i !== self.selectedInstitutionIndex) {
                item.itemStyle = { ...(item.itemStyle || {}), opacity: 0.2 }
              }
              return item
            }),
          }))
          : series,
      }
    },

    // ── Schedule chart: X axis = institutions, 2 bars per institution (green=interna, blue=total) ──
    scheduleChartOption() {
      const d = this.groupedData
      const instNames = d.institutions.map((i) => i.institutionName)

      // Aggregate each institution's percentage across all months
      const intPcts = d.institutions.map((inst) => {
        let sch = 0, exe = 0
        ;(inst.dataByMonth || []).forEach((m) => { sch += m.internalSchedule || 0; exe += m.internalExecuted || 0 })
        return sch > 0 ? +(exe / sch * 100).toFixed(2) : 0
      })
      const totalPcts = d.institutions.map((inst) => {
        let sch = 0, exe = 0
        ;(inst.dataByMonth || []).forEach((m) => { sch += m.totalSchedule || 0; exe += m.totalExecuted || 0 })
        return sch > 0 ? +(exe / sch * 100).toFixed(2) : 0
      })

      return this.buildBarOption(
        instNames,
        [
          {
            name: '% de conclusão interna', type: 'bar',
            data: intPcts.map((pct, i) => ({ value: pct, _raw: this.getRawScheduleData(d.institutions[i], 'internal') })),
            itemStyle: { color: COLOR_INTERNAL, borderRadius: [2, 2, 0, 0] },
            barMaxWidth: 28,
            barGap: '30%',
          },
          {
            name: '% de conclusão total', type: 'bar',
            data: totalPcts.map((pct, i) => ({ value: pct, _raw: this.getRawScheduleData(d.institutions[i], 'total') })),
            itemStyle: { color: COLOR_TOTAL, borderRadius: [2, 2, 0, 0] },
            barMaxWidth: 28,
          },
        ],
        ['% de conclusão interna', '% de conclusão total'],
        { yFormatter: '{value}%' },
      )
    },

    scheduleTableRows() {
      return this.filteredInstitutions.map((inst) => {
        let intSch = 0, intExe = 0, totalSch = 0, totalExe = 0
        ;(inst.dataByMonth || []).forEach((m) => {
          intSch += m.internalSchedule || 0; intExe += m.internalExecuted || 0
          totalSch += m.totalSchedule || 0; totalExe += m.totalExecuted || 0
        })
        const intPct = intSch > 0 ? (intExe / intSch * 100).toFixed(2) : '0.00'
        const totalPct = totalSch > 0 ? (totalExe / totalSch * 100).toFixed(2) : '0.00'
        return {
          label: inst.institutionName,
          values: [intSch, intExe, intPct + '%', totalSch, totalExe, totalPct + '%'],
          class: '',
        }
      })
    },

    getRawScheduleData(inst, type) {
      let sch = 0, exe = 0
      ;(inst.dataByMonth || []).forEach((m) => {
        if (type === 'internal') { sch += m.internalSchedule || 0; exe += m.internalExecuted || 0 }
        else { sch += m.totalSchedule || 0; exe += m.totalExecuted || 0 }
      })
      return { scheduled: sch, executed: exe }
    },

    getRawResolvabilityData(inst) {
      let totalInt = 0, totalOs = 0
      ;(inst.dataByMonth || []).forEach((m) => {
        totalInt += m.totalInternal || 0; totalOs += m.totalServiceOrders || 0
      })
      return { internal: totalInt, total: totalOs }
    },

    resolvabilityChartOption() {
      const d = this.groupedData
      const instNames = d.institutions.map((i) => i.institutionName)

      const pcts = d.institutions.map((inst) => {
        const raw = this.getRawResolvabilityData(inst)
        return raw.total > 0 ? +(raw.internal / raw.total * 100).toFixed(2) : 0
      })

      return this.buildBarOption(
        instNames,
        [
          {
            name: 'Resolutividade da Equipe', type: 'bar',
            data: pcts.map((pct, i) => ({ value: pct, _raw: this.getRawResolvabilityData(d.institutions[i]) })),
            itemStyle: { color: COLOR_INTERNAL, borderRadius: [2, 2, 0, 0] },
            barMaxWidth: 28,
          },
        ],
        ['Resolutividade da Equipe'],
        { yFormatter: '{value}%' },
      )
    },

    resolvabilityTableRows() {
      return this.filteredInstitutions.map((inst) => {
        let totalInt = 0, totalOs = 0
        ;(inst.dataByMonth || []).forEach((m) => {
          totalInt += m.totalInternal || 0; totalOs += m.totalServiceOrders || 0
        })
        const pct = totalOs > 0 ? (totalInt / totalOs * 100).toFixed(2) : '0.00'
        return {
          label: inst.institutionName,
          values: [totalInt, totalOs, pct + '%'],
          class: '',
        }
      })
    },

    // ── Satisfaction chart ──
    satisfactionChartOption() {
      const d = this.groupedData
      const instNames = d.institutions.map((i) => i.institutionName)

      const satPcts = d.institutions.map((inst) => {
        const total = inst.totalRatings || 0
        const good = (inst.highRating || 0) + (inst.wellRating || 0)
        return total > 0 ? +(good / total * 100).toFixed(2) : 0
      })

      return this.buildBarOption(
        instNames,
        [
          {
            name: '% de satisfação', type: 'bar',
            data: satPcts.map((pct, i) => {
              const inst = d.institutions[i]
              return { value: pct, _raw: { high: inst.highRating, well: inst.wellRating, regular: inst.regularRating, bad: inst.badRating, total: inst.totalRatings } }
            }),
            itemStyle: { color: COLOR_INTERNAL, borderRadius: [2, 2, 0, 0] },
            barMaxWidth: 28,
          },
        ],
        ['% de satisfação'],
        { yFormatter: '{value}%' },
      )
    },

    satisfactionTableRows() {
      return this.filteredInstitutions.map((inst) => {
        const total = inst.totalRatings || 0
        const good = (inst.highRating || 0) + (inst.wellRating || 0)
        const pct = total > 0 ? (good / total * 100).toFixed(2) : '0.00'
        return {
          label: inst.institutionName,
          values: [inst.highRating, inst.wellRating, inst.regularRating, inst.badRating, total, pct + '%'],
          class: '',
        }
      })
    },

    // ── Formatters ──
    formatMonthShort(d) { return moment(d).format('MM/YYYY') },
    formatMonth(d) { return moment(d).locale(this.$i18n.locale).format('MMMM/YYYY') },

    // ── Data fetching ──
    async fetchTabData() {
      const cfg = this.activeTabConfig
      this.chartLoading = true
      this.groupedData = null
      this.adhesionCalledData = null
      this.selectedInstitutionIndex = null
      try {
        const startDate = moment(this.chartFilter.startDate).startOf('month').format('YYYY-MM-DD')
        const endDate = moment(this.chartFilter.endDate).endOf('month').format('YYYY-MM-DD')
        const payload = {
          institutionIds: this.chartFilter.institutionIds,
          unidadeIds: this.chartFilter.unidadeIds,
          startDate,
          endDate,
        }

        if (cfg.type === 'satisfaction') {
          const adhPayload = {
            ...payload,
            startDate: moment(this.chartFilter.startDate).startOf('month').format('YYYY-MM-DD'),
            endDate: moment(this.chartFilter.endDate).endOf('month').format('YYYY-MM-DD'),
          }
          this.adhesionToastId = this.$toast.info(
            this.$t('Devido ao volume de dados poderá demorar alguns segundos.'),
            { timeout: false, closeOnClick: false }
          )
          const [r, rAdh] = await Promise.all([
            this.apiService.post(cfg.endpoint, payload),
            this.apiService.post('bigData/grouped/adhesionCalled', adhPayload),
          ])
          this.groupedData = Object.freeze(r.content)
          this.adhesionCalledData = Object.freeze(rAdh.content)
        } else {
          const r = await this.apiService.post(cfg.endpoint, payload)
          this.groupedData = Object.freeze(r.content)
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.chartLoading = false
        if (this.adhesionToastId !== null) {
          this.$toast.dismiss(this.adhesionToastId)
          this.adhesionToastId = null
        }
      }
    },

    applyFilter() { this.fetchTabData(); this.getTexts() },

    resetFilter() {
      this.selectedAdminUserId = null
      this.selectedSupervisorId = null
      this.chartFilter = {
        institutionIds: [], unidadeIds: [],
        startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      }
      this.unidades = []
      this.fetchTabData()
      this.getTexts()
    },

    async getInstitutions() {
      this.institutionLoading = true
      try {
        const r = await this.apiService.get('institution/list')
        this.institutions = r.content
        if (this.isAdminOrMaster) {
          this.getSupervisors()
          this.getAdminUsers()
        }
      } catch (e) { console.error(e) } finally { this.institutionLoading = false }
    },

    async getSupervisors() {
      this.supervisorsLoading = true
      try {
        const allIds = this.institutions.map((i) => i.id)
        const r = await this.apiService.post('bigData/users', allIds)
        this.supervisors = r.content || []
      } catch (e) { console.error(e) } finally { this.supervisorsLoading = false }
    },

    async getAdminUsers() {
      this.adminUsersLoading = true
      try {
        const allIds = this.institutions.map((i) => i.id)
        const r = await this.apiService.post('bigData/admin-users', allIds)
        this.adminUsers = (r.content || []).map((u) => ({
          ...u,
          displayName: u.name + ' (' + u.role + ')',
        }))
      } catch (e) { console.error(e) } finally { this.adminUsersLoading = false }
    },

    async onAdminUserChange() {
      this.selectedSupervisorId = null
      this.chartFilter.unidadeIds = []
      this.unidades = []
      if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.hasPreset && adminUser.presetInstitutionIds) {
          this.chartFilter.institutionIds = [...adminUser.presetInstitutionIds]
          await this.getUnidades()
          if (adminUser.presetUnidadeIds && adminUser.presetUnidadeIds.length) {
            this.chartFilter.unidadeIds = [...adminUser.presetUnidadeIds]
          } else {
            this.chartFilter.unidadeIds = this.unidades.map((u) => u.id)
          }
          this.applyFilter()
          return
        } else {
          this.chartFilter.institutionIds = []
        }
      } else {
        this.chartFilter.institutionIds = []
      }
      this.getUnidades(true)
    },

    onSupervisorChange() {
      this.chartFilter.unidadeIds = []
      this.unidades = []
      if (this.selectedSupervisorId) {
        const supervisor = this.supervisors.find((s) => s.id === this.selectedSupervisorId)
        this.chartFilter.institutionIds = supervisor ? [...supervisor.institutionIds] : []
      } else if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.hasPreset && adminUser.presetInstitutionIds) {
          this.chartFilter.institutionIds = [...adminUser.presetInstitutionIds]
        } else {
          this.chartFilter.institutionIds = adminUser ? [...adminUser.institutionIds] : []
        }
      } else {
        this.chartFilter.institutionIds = []
      }
      this.getUnidades(true)
    },

    async savePreset() {
      if (!this.selectedAdminUserId || !this.chartFilter.institutionIds.length) return
      this.savingPreset = true
      try {
        await this.apiService.post('dashboardFilterPreset', {
          userId: this.selectedAdminUserId,
          institutionIds: this.chartFilter.institutionIds,
          unidadeIds: this.chartFilter.unidadeIds,
        })
        const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (user) {
          user.presetInstitutionIds = [...this.chartFilter.institutionIds]
          user.presetUnidadeIds = [...this.chartFilter.unidadeIds]
          user.hasPreset = true
        }
        this.$toast.success('Preset salvo com sucesso!')
      } catch (e) {
        console.error(e)
        this.$toast.error('Erro ao salvar preset')
      } finally {
        this.savingPreset = false
      }
    },

    async removePreset() {
      if (!this.selectedAdminUserId) return
      this.savingPreset = true
      try {
        await this.apiService.delete('dashboardFilterPreset/' + this.selectedAdminUserId)
        const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (user) {
          user.presetInstitutionIds = null
          user.presetUnidadeIds = null
          user.hasPreset = false
        }
        this.$toast.success('Preset removido com sucesso!')
      } catch (e) {
        console.error(e)
        this.$toast.error('Erro ao remover preset')
      } finally {
        this.savingPreset = false
      }
    },

    async getUnidades(autoSelectAll = false) {
      if (!this.chartFilter.institutionIds.length) { this.unidades = []; this.chartFilter.unidadeIds = []; return }
      this.unidadeLoading = true
      try {
        const r = await this.apiService.post('unidade/listByIntitutionIds', { ids: this.chartFilter.institutionIds })
        this.unidades = r.content
        if (autoSelectAll) this.chartFilter.unidadeIds = r.content.map((u) => u.id)
      }
      catch (e) { console.error(e) } finally { this.unidadeLoading = false }
    },

    // ── Análises ──
    openRemoveDialog(item) { this.analysis = item; this.removeDialog = true },
    remove() {
      this.apiService.delete('bigDataDashboardText/remove?id=' + this.analysis.id).then(() => {
        this.getTexts(); this.$toast.success('Análise removida com sucesso!'); this.removeDialog = false
      })
    },
    openViewDialog(item) { this.analysis = item; this.viewDialog = true },
    openEditDialog(item) { this.analysis = item; this.manageDialog = true },
    openAddDialog() {
      this.analysis = {
        authorId: this.loggedUserId,
        reportDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        text: null, type: this.reportTab,
      }
      this.manageDialog = true
    },
    submit() {
      if (this.$refs.form.validate()) {
        const m = this.analysis; this.loading = true
        const url = m.id ? 'bigDataDashboardText/edit' : 'bigDataDashboardText/add'
        const msg = m.id ? 'Análise atualizada com sucesso!' : 'Análise registrada! 🧠'
        this.apiService.post(url, m).then(() => {
          this.getTexts(); this.$toast.success(msg); this.loading = false; this.manageDialog = false
        }).catch(() => { this.loading = false })
      }
    },
    getTexts() {
      this.filter.type = this.reportTab; this.filter.page = this.page; this.filter.pageSize = this.pageSize
      const payload = {
        ...this.filter,
        startDate: moment(this.filter.startDate).startOf('month').format('YYYY-MM-DD'),
        endDate: moment(this.filter.endDate).endOf('month').format('YYYY-MM-DD'),
      }
      this.apiService.post('bigDataDashboardText/list', payload).then((r) => {
        this.items = r.content.data; this.totalItems = r.content.pager.totalItems
        this.totalPages = r.content.pager.totalPages; this.totalItensOnPage = r.content.data.length
      })
    },
  },

  watch: {
    reportTab() { this.page = 1; this.fetchTabData(); this.getTexts() },
    page() { this.getTexts() },
    pageSize() { this.getTexts() },
  },
}
</script>

<style scoped>
/* ── Tabs ── */
.v-tabs--icons-and-text > .v-tabs-bar .v-tab {
  flex-direction: row-reverse;
}
.v-tab.v-tab {
  color: #bbcf20;
  text-transform: none;
}
.v-tab {
  font-family: 'Ubuntu';
  font-weight: bold;
}

/* ── Dark panel ── */
.dark-panel {
  background: linear-gradient(160deg, #0a2a4a 0%, #0f3565 40%, #122e50 100%);
  border-radius: 0 0 12px 12px;
  padding: 20px;
  min-height: 300px;
  position: relative;
  overflow: hidden;
}

/* ── Filter inputs ── */
.panel-label {
  display: block;
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.panel-input >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 13px;
  align-items: center;
  padding: 0 10px !important;
}
.panel-input >>> .v-select__selections {
  min-height: 36px !important;
  padding: 2px 0 !important;
  align-items: center;
}
.panel-input >>> input {
  font-size: 13px;
  padding: 4px 0 !important;
  max-height: 36px;
}
.panel-input >>> .v-input__append-inner,
.panel-input >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center;
}
.panel-input >>> .v-input__append-inner .v-icon,
.panel-input >>> .v-input__prepend-inner .v-icon {
  font-size: 18px;
}
.panel-input >>> fieldset {
  border-color: rgba(255, 255, 255, 0.2) !important;
  transition: border-color 0.2s;
}
.panel-input >>> .v-input__slot:hover fieldset {
  border-color: rgba(255, 255, 255, 0.45) !important;
}
.panel-input >>> .v-input--is-focused fieldset,
.panel-input.v-input--is-focused >>> fieldset {
  border-color: #bbcf20 !important;
  border-width: 1px !important;
}

/* ── Title ── */
.panel-title {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

/* ── KPI Cards ── */
.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}
.kpi-card {
  text-align: center;
  min-width: 100px;
  flex: 1 1 0;
  max-width: 200px;
}
.kpi-value {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  font-family: 'Ubuntu', sans-serif;
}
.kpi-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ── Gauge ── */
.gauge-title {
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.gauge-chart {
  width: 200px;
  height: 160px;
}

/* ── Chart subtitle ── */
.chart-subtitle {
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ── Scrollable chart container ── */
.chart-scroll-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 6px;
  padding-bottom: 4px;
}

/* ── Main bar chart ── */
.main-bar-chart { height: 380px; }

.adhesion-gauge {
  width: 380px;
  height: 380px;
}
.adhesion-gauge-chart {
  width: 280px;
  height: 380px;
}

/* ── Panel data table ── */
.panel-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  max-height: 400px;
  overflow-y: auto;
}
.panel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.panel-table thead th {
  background: rgba(187, 207, 32, 0.25);
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 8px 14px;
  white-space: nowrap;
  border-bottom: 2px solid rgba(187, 207, 32, 0.4);
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 2;
}
.panel-table thead th.sticky-col {
  text-align: left;
  z-index: 3;
}
.panel-table tbody td {
  color: rgba(255, 255, 255, 0.85);
  padding: 6px 14px;
  white-space: nowrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.panel-table .sticky-col {
  position: sticky;
  left: 0;
  background: #0d2f55;
  z-index: 1;
  min-width: 180px;
  max-width: 250px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}
.panel-table thead .sticky-col {
  background: rgba(15, 53, 101, 0.95);
  z-index: 3;
}
.panel-table tbody tr:hover td {
  background: rgba(255, 255, 255, 0.04);
}

/* ── Scrollbar styling ── */
.chart-scroll-wrapper::-webkit-scrollbar,
.dark-panel ::-webkit-scrollbar,
.panel-table-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.chart-scroll-wrapper::-webkit-scrollbar-track,
.dark-panel ::-webkit-scrollbar-track,
.panel-table-wrapper::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}
.chart-scroll-wrapper::-webkit-scrollbar-thumb,
.dark-panel ::-webkit-scrollbar-thumb,
.panel-table-wrapper::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.chart-scroll-wrapper::-webkit-scrollbar-thumb:hover,
.dark-panel ::-webkit-scrollbar-thumb:hover,
.panel-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* Firefox */
.chart-scroll-wrapper,
.dark-panel,
.panel-table-wrapper {
  scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
  scrollbar-width: thin;
}

/* ── Transitions ── */
.fade-content-enter-active,
.fade-content-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-content-enter {
  opacity: 0;
  transform: translateY(6px);
}
.fade-content-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.fade-title-enter-active,
.fade-title-leave-active {
  transition: opacity 0.18s ease;
}
.fade-title-enter,
.fade-title-leave-to {
  opacity: 0;
}
</style>
